/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: /home/salvo/Desktop/collegamenti_utili/software_dev/workspace/coronatek-v2/frontend/src/components/ui/QRCodeWithLogo/QRCodeWithLogo.tsx
 * Release: V2.0.0 - Componente QR Code con Logo Coronatek
 * Descrizione: Componente per generare QR code con logo Coronatek per codici cliente
 */

import React from 'react';
import { QRCodeSVG } from 'qrcode.react';

interface QRCodeWithLogoProps {
  value: string;
  size?: number;
  logoSize?: number;
  logoUrl?: string;
  downloadable?: boolean;
  customerCode?: string;
}

export const QRCodeWithLogo: React.FC<QRCodeWithLogoProps> = ({
  value,
  size = 200,
  logoSize = 40,
  logoUrl = '/assets/Coronatek_Logo_92dpi_200x88.png',
  downloadable = false,
  customerCode
}) => {
  const downloadQRCode = () => {
    if (!downloadable) return;

    // Crea canvas per il download
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = size;
    canvas.height = size;
    
    // Sfondo bianco
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, size, size);

    // Converti SVG in immagine e scarica
    const svgElement = document.querySelector(`#qr-${customerCode || 'default'}`) as SVGElement;
    if (svgElement) {
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const svgBlob = new Blob([svgData], {type: 'image/svg+xml;charset=utf-8'});
      const url = URL.createObjectURL(svgBlob);
      
      const link = document.createElement('a');
      link.download = `Coronatek_QR_${customerCode || 'code'}.svg`;
      link.href = url;
      link.click();
      
      URL.revokeObjectURL(url);
    }
  };

  return (
    <div className="relative inline-block">
      <QRCodeSVG
        id={`qr-${customerCode || 'default'}`}
        value={value}
        size={size}
        level="H" // High error correction per logo overlay
        includeMargin={true}
        bgColor="#ffffff"
        fgColor="#000000"
      />
      
      {/* Logo overlay centrato */}
      <div 
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
        style={{
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: logoSize,
          height: logoSize,
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}
      >
        <img
          src={logoUrl}
          alt="Coronatek Logo"
          className="w-full h-full object-contain"
          onError={(e) => {
            // Fallback se logo non carica
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
          }}
        />
      </div>

      {/* Bottone download se abilitato */}
      {downloadable && (
        <button
          onClick={downloadQRCode}
          className="absolute top-2 right-2 bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg shadow-lg transition-colors duration-200"
          title="Scarica QR Code"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
          </svg>
        </button>
      )}
      
      {/* Info codice cliente */}
      {customerCode && (
        <div className="text-center mt-2">
          <span className="text-xs text-zinc-400 font-mono">{customerCode}</span>
        </div>
      )}
    </div>
  );
};

export default QRCodeWithLogo;
